import { __rest } from '../../../../node_modules/tslib/tslib.es6.js';
import { jsx } from 'react/jsx-runtime';
import React__default, { useRef, useCallback, useImperativeHandle, useMemo } from 'react';
import cns from '../../../../node_modules/classnames/index.js';
import { NOOP } from '../../../../utils/const.js';
import Validation from '../../../Validation/index.js';
import TextField from '../../FieldControlled/TextField/TextField.js';
import { getInputFormat, createFocus, createContains } from '../../FieldControlled/shared.js';
import Restriction from '../../../Restriction/Restriction.js';
import { Align } from '../../../Utils/numeriHelper.js';
import { isConfirmOnBlur, isConfirmOnEditing } from '../../FieldControlled/common.js';
import useFieldValue from '../hooks/useFieldValue.js';
import useComponentStateWithValidations from '../hooks/useComponentStateWithValidations.js';
import useInputText from './useInputText.js';
import useFormReference from '../hooks/useFormReference.js';
import useDebouncedValueChange from '../hooks/useDebouncedValueChange.js';
import useFormValueChanged from '../hooks/useFormValueChanged.js';
import useFormConfirmMode from '../hooks/useFormConfirmMode.js';
import { EmphasisLM } from '../../../types.js';

function useInputReference(value, text, validation, setValue, setTouched, setTextAndValue, setFormValidation) {
    const inputRef = {
        setValue: (newValue) => {
            setValue(newValue, false);
        },
        changeValue: (newValue) => {
            setValue(newValue, false);
            setTouched(true);
        },
        initializeValue: (newValue) => {
            setValue(newValue, true);
            setTouched(false);
        },
        getValue: () => value,
        validate: () => {
            setTextAndValue(text);
        },
        getValidation: () => validation,
        setFormValidation,
    };
    const ref = useRef(inputRef);
    ref.current = inputRef;
    return ref;
}
const InputComp = React__default.forwardRef((_a, ref) => {
    var { className, inputProps, validations, formFieldName, format, precision, confirmMode: confirmModeDefault, onBlur = NOOP, onChangeValue, onChange } = _a, textFieldProps = __rest(_a, ["className", "inputProps", "validations", "formFieldName", "format", "precision", "confirmMode", "onBlur", "onChangeValue", "onChange"]);
    const confirmMode = useFormConfirmMode(confirmModeDefault);
    const textFieldRef = useRef(null);
    const notifyValueChange = useFormValueChanged(formFieldName, onChangeValue);
    const [state, stateMessage, stateToShow, stateMessageToShow, updateState, setTouched, setFormValidation,] = useComponentStateWithValidations(formFieldName, notifyValueChange, validations, getInputFormat(format).equals);
    const { text, onChangeText: handleTextChange } = useInputText(textFieldRef, onChange);
    const handleValueChanged = useCallback((newValue, isOnInitialization, event) => {
        const inputFormatter = getInputFormat(format);
        const newText = inputFormatter.format(newValue, precision);
        handleTextChange(newText, event);
        updateState(newValue, isOnInitialization);
    }, [format, precision, handleTextChange, updateState]);
    const [value, setValue] = useFieldValue(handleValueChanged);
    const handleTextValueChange = useCallback((textValue, evt) => {
        const inputFormatter = getInputFormat(format);
        const newValue = inputFormatter.parse(textValue, precision);
        setValue(newValue, false, evt);
        setTouched(true);
    }, [format, precision, setTouched, setValue]);
    const handleTextFieldChange = useDebouncedValueChange(handleTextChange, handleTextValueChange);
    const reference = useInputReference(value, text, { state, stateMessage }, setValue, setTouched, handleTextValueChange, setFormValidation);
    useFormReference(formFieldName, reference);
    useImperativeHandle(ref, () => (Object.assign(Object.assign({}, reference.current), { focus: createFocus(textFieldRef), contains: createContains(textFieldRef) })), [reference.current]);
    const redefInputProps = useMemo(() => (Object.assign(Object.assign({}, inputProps), { onBlur: (event) => {
            // eslint-disable-next-line no-bitwise
            if (isConfirmOnBlur(confirmMode)) {
                handleTextValueChange(event.currentTarget.value);
            }
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
        } })), [confirmMode, handleTextValueChange, inputProps, onBlur]);
    return (jsx(TextField, Object.assign({}, textFieldProps, { className: cns('v2-controlled-input', className), precision: precision, ref: textFieldRef, format: format, inputProps: redefInputProps, onChange: isConfirmOnEditing(confirmMode) ? handleTextFieldChange : handleTextChange, value: text, state: stateToShow, stateMessage: stateMessageToShow })));
});
InputComp.defaultProps = {
    action: undefined,
    align: Align.left,
    className: undefined,
    disabled: false,
    onActionClick: NOOP,
    onBlur: NOOP,
    onChange: NOOP,
    onChangeValue: NOOP,
    onFormInitValue: NOOP,
    onKeyDown: undefined,
    placeholder: undefined,
    readOnly: false,
    title: undefined,
    titleIcon: undefined,
    icon: undefined,
    inputProps: undefined,
    format: undefined,
    restrictions: undefined,
    validations: undefined,
    precision: undefined,
    formFieldName: undefined,
    helperText: undefined,
    componentHeight: undefined,
    emphasis: EmphasisLM.MEDIUM,
    autoConfirmValue: true,
    tooltipProps: undefined,
    confirmMode: undefined,
    flowMode: undefined,
};
/**
Componente per l'inserimento di un valore "formattato".

Vedi [Design System di CGN - Campi di Input](https://zeroheight.com/1b5e94371/p/940c42-campi-di-input/b/003896)

Al blur il valore inserito è analizzato in base al formato e alle regole di validazione è restituito (vedi porprietà onChangeValue).

Ref:
- `setValue`: funzione per cambiare il valore dell'input.
- `initializeValue`: funzione per inizializzare il valore dell'input. Cambia lo stato di touched del componente.
- `changeValue`: funzione per inizializzare il valore dell'input. Cambia lo stato di touched del componente.
- `getValue`: funzione per inizializzare il valore dell'input. Cambia lo stato di touched del componente.
- `validate`: valida il valore attualmente inserito.
- `focus`: funzione per tentare di acquisire il focus.
- `contains`: funzione per verificare se il componente "contiene" un altro componente nel suo sotto albero.
- `confirm`: conferma il valore del componente.

Constants:
- `Input.Formats`: formati ammessi per la proprietà `format`;
- `Input.States`: stati ammessi per la proprietà `state` di validations;
- `Input.Validations`: validazioni fornite dal framework (vedi proprietà `validations`)
- `Input.Restrictions`: restrizioni fornite dal framework (vedi proprietà `restrictions`)

Utilities:
- `Input.valueToString`: funzione per convertira un valore nella sua rappresentazione testuale (`valueToString(value, format, precision)`)
- `Input.stringToValue`: funzione per convertira la rappresentazione testuale in un valore (`stringToValue(text, format, precision)`)
*/
const Input = InputComp;
Input.valueToString = function valueToString(value, format, precision) {
    return getInputFormat(format).format(value, precision);
};
Input.stringToValue = function stringToValue(text, format, precision) {
    return getInputFormat(format).parse(text, precision);
};
Input.Formats = TextField.Formats;
Input.States = TextField.States;
Input.Validations = Validation.DefaultRules;
Input.Restrictions = Restriction.DefaultValues;

export { Input as default };
